<div class="page">
    <div class="page-head">
        <div>
            <h2>{{ pageTitle() }}</h2>
            <p>Assign menus to roles, and grant or deny menus per user. A <b>deny</b> always wins over a role grant.</p>
        </div>
    </div>

    <!-- Tabs -->
    <div class="tabs">
        <button type="button" class="tab" [class.active]="tab() === 'role'" (click)="setTab('role')">
            <i class="pi pi-users"></i> Role → Menus
        </button>
        <button type="button" class="tab" [class.active]="tab() === 'user'" (click)="setTab('user')">
            <i class="pi pi-user"></i> User → Menus
        </button>
    </div>

    <!-- recursive checkbox tree for a single selection set (role) -->
    <ng-template #roleTree let-nodes let-depth="depth">
        @for (n of nodes; track n.menuId) {
            <label class="tree-item" [style.paddingLeft.px]="12 + depth * 20">
                <p-checkbox [binary]="true" [ngModel]="checked('role', n.menuId)"
                    (onChange)="onToggle('role', n.menuId)" />
                <span class="ti-title">
                    <i [class]="n.icon || 'pi pi-circle'"></i> {{ n.title }}
                    @if (n.route) { <small class="mono">{{ n.route }}</small> } @else { <small class="muted">group</small> }
                </span>
            </label>
            @if (n.children?.length) {
                <ng-container *ngTemplateOutlet="roleTree; context: { $implicit: n.children, depth: depth + 1 }"></ng-container>
            }
        }
    </ng-template>

    <!-- recursive grant/deny tree (user) -->
    <ng-template #userTree let-nodes let-depth="depth">
        @for (n of nodes; track n.menuId) {
            <div class="tree-item two" [style.paddingLeft.px]="12 + depth * 20">
                <span class="ti-title">
                    <i [class]="n.icon || 'pi pi-circle'"></i> {{ n.title }}
                    @if (n.route) { <small class="mono">{{ n.route }}</small> } @else { <small class="muted">group</small> }
                </span>
                <label class="chk grant" [class.on]="checked('grant', n.menuId)">
                    <p-checkbox [binary]="true" [ngModel]="checked('grant', n.menuId)"
                        (onChange)="onToggle('grant', n.menuId)" /> Grant
                </label>
                <label class="chk deny" [class.on]="checked('deny', n.menuId)">
                    <p-checkbox [binary]="true" [ngModel]="checked('deny', n.menuId)"
                        (onChange)="onToggle('deny', n.menuId)" /> Deny
                </label>
            </div>
            @if (n.children?.length) {
                <ng-container *ngTemplateOutlet="userTree; context: { $implicit: n.children, depth: depth + 1 }"></ng-container>
            }
        }
    </ng-template>

    <!-- read-only effective menu preview -->
    <ng-template #previewTree let-nodes let-depth="depth">
        @for (n of nodes; track n.menuId) {
            <div class="prev-item" [style.paddingLeft.px]="8 + depth * 18">
                <i [class]="n.icon || 'pi pi-circle'"></i> {{ n.title }}
            </div>
            @if (n.children?.length) {
                <ng-container *ngTemplateOutlet="previewTree; context: { $implicit: n.children, depth: depth + 1 }"></ng-container>
            }
        }
    </ng-template>

    <!-- ============ TAB 1: ROLE -> MENUS ============ -->
    @if (tab() === 'role') {
        <div class="card pad">
            <div class="loader-row">
                <div class="field">
                    <label>Role</label>
                    <p-select [ngModel]="roleId()" (ngModelChange)="roleId.set($event != null ? +$event : null)" [options]="roleOptions()" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" [showClear]="true" placeholder="All roles" appendTo="body" />
                </div>
                <p-button label="Load" icon="pi pi-search" [outlined]="true" (onClick)="loadRole()" />
                @if (roleLoaded()) {
                    <p-button styleClass="ml-auto" [label]="saving() ? 'Saving...' : 'Save Assignments'"
                        icon="pi pi-check" [loading]="saving()" (onClick)="saveRole()" />
                }
            </div>

            @if (loading()) {
                <div class="state"><i class="pi pi-spin pi-spinner"></i> Loading...</div>
            } @else if (roleLoaded()) {
                <p class="hint">Check the menus this role should see ({{ roleSelected().size }} selected).</p>
                <div class="tree-wrap">
                    <ng-container *ngTemplateOutlet="roleTree; context: { $implicit: menuTree(), depth: 0 }"></ng-container>
                </div>
            } @else {
                <div class="state"><i class="pi pi-users"></i><p>Select a role and click Load to manage its menus.</p></div>
            }
        </div>
    }

    <!-- ============ TAB 2: USER -> MENUS ============ -->
    @if (tab() === 'user') {
        <div class="card pad">
            <div class="loader-row">
                <div class="field">
                    <label>User</label>
                    <p-select [ngModel]="userId()" (ngModelChange)="userId.set($event != null ? +$event : null)" [options]="userOptions()" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" [showClear]="true" placeholder="All users" appendTo="body" />
                </div>
                <p-button label="Load" icon="pi pi-search" [outlined]="true" (onClick)="loadUser()" />
                @if (userLoaded()) {
                    <p-button styleClass="ml-auto" [label]="saving() ? 'Saving...' : 'Save Overrides'"
                        icon="pi pi-check" [loading]="saving()" (onClick)="saveUser()" />
                }
            </div>

            @if (loading()) {
                <div class="state"><i class="pi pi-spin pi-spinner"></i> Loading...</div>
            } @else if (userLoaded()) {
                <p class="hint">
                    <b>Grant</b> adds a menu just for this user; <b>Deny</b> blocks a menu the user's role grants.
                    Grant {{ grantSelected().size }} · Deny {{ denySelected().size }}.
                </p>
                <div class="user-grid">
                    <div class="tree-wrap">
                        <div class="tree-head"><span>Menu</span><span>Grant</span><span>Deny</span></div>
                        <ng-container *ngTemplateOutlet="userTree; context: { $implicit: menuTree(), depth: 0 }"></ng-container>
                    </div>

                    <aside class="preview">
                        <div class="preview-head">
                            <strong>Resulting menu</strong>
                            @if (previewLoading()) { <i class="pi pi-spin pi-spinner"></i> }
                        </div>
                        @if (effectiveTree().length) {
                            <ng-container *ngTemplateOutlet="previewTree; context: { $implicit: effectiveTree(), depth: 0 }"></ng-container>
                        } @else if (!previewLoading()) {
                            <p class="muted sm">No menus resolve for this user yet.</p>
                        }
                    </aside>
                </div>
            } @else {
                <div class="state"><i class="pi pi-user"></i><p>Select a user and click Load to manage grant / deny overrides.</p></div>
            }
        </div>
    }
</div>
